import React, { useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons';
import { GTFonts, useTheme, type Theme } from '../../../theme';
import { fs, iconSize, s, v } from '../../../utils/libs';
import ActivationTaskRow from './ActivationTaskRow';

/**
 * Injected-task activation tracker shell.
 *
 * Presentation only: task definitions, completion state, collapse state, and
 * every action come in through props. Completion rules, selectors, and
 * analytics belong to the connected composition (OwnerGuidanceSlot/Home).
 */

export type OwnerActivationTrackerTask = {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  highlighted: boolean;
  ctaLabel?: string;
  dependencyNote?: string;
  disabled?: boolean;
  busy?: boolean;
  onPress?: () => void;
};

export type OwnerActivationTrackerProps = {
  tasks: OwnerActivationTrackerTask[];
  completedCount: number;
  totalCount: number;
  collapsed: boolean;
  onToggleCollapsed: (collapsed: boolean) => void;
  /** Completed-unacknowledged/acknowledged completion headline override. */
  completionHeadline?: string;
  /** Optional loading state before completion evidence has loaded. */
  loading?: boolean;
  testID?: string;
};

const OwnerActivationTracker: React.FC<OwnerActivationTrackerProps> = ({
  tasks,
  completedCount,
  totalCount,
  collapsed,
  onToggleCollapsed,
  completionHeadline,
  loading = false,
  testID,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const allComplete = totalCount > 0 && completedCount >= totalCount;
  const progressLabel = `${completedCount} of ${totalCount} actions complete`;
  const progressRatio = totalCount > 0 ? completedCount / totalCount : 0;

  if (loading) {
    return (
      <View
        testID={testID}
        style={styles.container}
        accessibilityLabel="Getting Started, loading"
        accessibilityState={{ busy: true }}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>Getting Started</Text>
        </View>
        <Text style={styles.subtitle}>Checking your progress…</Text>
      </View>
    );
  }

  const isCollapsed = collapsed && !allComplete;

  return (
    <View testID={testID} style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.title} accessibilityRole="header">
          Getting Started
        </Text>
        {!allComplete && (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={
              isCollapsed ? 'Show actions' : 'Collapse Getting Started'
            }
            accessibilityHint={
              isCollapsed ? 'Expands the Getting Started actions' : undefined
            }
            onPress={() => onToggleCollapsed(!isCollapsed)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={styles.collapseButton}>
            <Ionicons
              name={isCollapsed ? 'chevron-down' : 'chevron-up'}
              size={iconSize(18)}
              color={theme.colors.textSecondary}
            />
          </TouchableOpacity>
        )}
      </View>

      <Text style={styles.subtitle}>
        {allComplete
          ? (completionHeadline ?? "You've explored the BizPlus essentials")
          : 'Your business is ready. Complete these actions to get the most from BizPlus POS.'}
      </Text>

      <View
        style={styles.progressRow}
        accessible
        accessibilityRole="progressbar"
        accessibilityLabel={progressLabel}
        accessibilityValue={{ min: 0, max: totalCount, now: completedCount }}>
        <View style={styles.progressTrack}>
          <View
            style={[
              styles.progressFill,
              { width: `${Math.round(progressRatio * 100)}%` },
              allComplete && styles.progressFillComplete,
            ]}
          />
        </View>
        <Text style={styles.progressText}>{progressLabel}</Text>
      </View>

      {!isCollapsed && (
        <View style={styles.taskList}>
          {tasks.map(task => (
            <ActivationTaskRow
              key={task.id}
              testID={`activation-task-${task.id}`}
              label={task.label}
              description={task.description}
              completed={task.completed}
              highlighted={task.highlighted}
              ctaLabel={task.ctaLabel}
              dependencyNote={task.dependencyNote}
              disabled={task.disabled}
              busy={task.busy}
              onPress={task.onPress}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(12),
      paddingTop: v(12),
      paddingBottom: v(12),
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 1,
      gap: v(8),
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      fontSize: fs(18),
      fontFamily: GTFonts.semibold,
      lineHeight: fs(22),
      color: theme.colors.text,
    },
    subtitle: {
      fontSize: fs(13),
      fontFamily: GTFonts.regular,
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    collapseButton: {
      padding: s(4),
    },
    progressRow: {
      gap: v(6),
    },
    progressTrack: {
      height: v(8),
      borderRadius: v(4),
      backgroundColor: theme.colors.border,
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      borderRadius: v(4),
      backgroundColor: theme.colors.success,
    },
    progressFillComplete: {
      backgroundColor: theme.colors.success,
    },
    progressText: {
      fontSize: fs(12),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    taskList: {
      gap: v(4),
    },
  });

export default OwnerActivationTracker;
